import { For, ParentProps, Show, createContext, createEffect, createMemo, createRoot, createSignal } from 'solid-js'
import { requireContext } from 'common'
import { reportResults as api, FieldRole, FieldRoleHint } from 'st-app/api'
import { ReportResultsContext } from './ReportResultsContext'
import styles from './st-report-results.module.css'
import { STComponentWrapperProps } from '../st-component-wrapper/st-component-wrapper'
import { DataGrid, PivotGrid, PivotGridFieldChooser } from 'dx-app'
import TabGroup from '../st-tab-group/st-tab-group'
import Tab from '../st-tab/st-tab'
import { showModal, useModalContext } from '../st-modal/st-modal'
import Toggle from '../st-toggle/st-toggle'

const ReportResultsContextJsx = createContext<ReportResultsContext>();
export const requireReportResultsContext = () => {
    return requireContext(ReportResultsContextJsx);
}

export class ReportResultsProps extends STComponentWrapperProps {
    componentName = 'st-report-results'
}

type Props = ParentProps<Omit<ReportResultsProps, 'componentName'>>;

export default function ReportResults(props: Props) {
    const modal = useModalContext();
    const processQueueGu = createMemo(() => props.value ?? '');

    const context = new ReportResultsContext(processQueueGu);

    createEffect(() => {
        if (context.processQueueGu()) {
            context.load();
        }
    })

    return (
        <ReportResultsContextJsx.Provider value={context}>
            <ReportResultsInternal {...props} />
        </ReportResultsContextJsx.Provider>
    )
}

function ReportResultsInternal(props: Props) {
    const context = requireReportResultsContext();

    return (
        <div>
            <ReportResultsDataGrid data={context.data()} />
        </div>
    );
}

function ReportResultsDataGrid(props: { data: any }) {
    const context = requireReportResultsContext();

    const [isInAdvancedMode, setIsInAdvancedMode] = createSignal(false);

    const store = new DevExpress.data.CustomStore({
        load: async () => {
            while (context.loading()) {
                await new Promise(resolve => setTimeout(resolve, 50));
            }
            return context.data() || [];
        },
    });

    const dataSource = new DevExpress.data.PivotGridDataSource({
        store: store,
    });

    return (
        <div style='max-width: 100% overflow-x: auto;'>
            <div class={styles['result-container']}>
                <div class={styles['result-actions']}>
                    <Toggle value={[isInAdvancedMode, setIsInAdvancedMode]} label="Advanced Mode" />
                </div>
                <div>
                    <Show when={isInAdvancedMode()}>
                        <AdvancedView dataSource={dataSource} />
                    </Show>
                    <Show when={!isInAdvancedMode()}>
                        <BasicView store={store} />
                    </Show>
                </div>
            </div>
        </div>
    );
}

const AdvancedView = (props: { dataSource: DevExpress.data.PivotGridDataSource }) => {
    const context = requireReportResultsContext();
    return (
        <div class={styles['advanced-mode-container']}>
            <PivotGrid
                export={{
                    enabled: true,
                }}
                showBorders
                fieldPanel={{
                    showFilterFields: true,
                    visible: true,
                }}
                allowFiltering
                fieldChooser={{
                    enabled: false,
                    layout: 2,
                }}     
                dataSource={props.dataSource}
                onCellClick={(e) => {
                    if (e.area !== 'data') return;
                    if (!e.cell) return;

                    const row = e.rowFields?.filter((f, i) => e.cell?.rowPath && i in e.cell.rowPath).map((f, i) => ({
                        label: f.caption,
                        value: e.cell?.rowPath?.[i] || '',
                    }))

                    const column = e.columnFields?.filter((f, i) => e.cell?.columnPath && i in e.cell.columnPath).map((f, i) => ({
                        label: f.caption,
                        value: e.cell?.columnPath?.[i] || '',
                    }))

                    const title = 'Details for ' + [
                        ...(row ? row.map(r => `${r.label}: ${r.value}`) : []),
                        ...(column ? column.map(c => `${c.label}: ${c.value}`) : []),
                    ].join(', ');


                    const dataSource = e.component.getDataSource();
                    const drillDownDataSource = dataSource.createDrillDownDataSource(e.cell);
                    drillDownDataSource.paginate(true);
                    drillDownDataSource.pageIndex(0);
                    drillDownDataSource.pageSize(10);

                    const fields = dataSource.fields();
                    const scrollFields = context.scrollFields().filter(sf => fields.some(f => f.dataField === sf));

                    const isPriorityField = (field: DevExpress.data.PivotGridDataSourceField) => {
                        if (e.dataFields?.some(f => f.dataField === field.dataField)) return true;
                        if (e.columnFields?.includes(field)) return true;
                        if (e.rowFields?.includes(field)) return true;
                        if (scrollFields.length === 0) return true;
                        if (field.dataField && scrollFields.includes(field.dataField)) return true;
                        return false;
                    }

                    showModal(() => {
                        const [showAll, setShowAll] = createSignal(scrollFields.length === 0);

                        return (
                            <div style={{ width: '80vw', height: '100%', 'max-height': '80vh', 'overflow-y': 'auto' }}>
                                 <DataGrid
                                    columnHidingEnabled={true}
                                    dataSource={drillDownDataSource}
                                    paging={{ enabled: true, pageSize: 10, pageIndex: 0 }}
                                    showBorders={true}
                                    export={{
                                        enabled: true,
                                    }}
                                    
                                >
                                    <DataGrid.Toolbar>
                                        <DataGrid.PredefinedToolbarItem name='exportButton' />
                                        <DataGrid.ToolbarItem name='toggle-show-all'>
                                            <Toggle disabled={scrollFields.length === 0} value={[showAll, setShowAll]} label="Show All Fields" />
                                        </DataGrid.ToolbarItem>
                                    </DataGrid.Toolbar>

                                    <For each={fields}>{field => (
                                        <DataGrid.Column
                                            dataField={field.dataField!}
                                            visible={showAll() || isPriorityField(field)}
                                        />
                                    )}</For>
                                </DataGrid>
                            </div>
                        )
                    }, {
                        title: title,
                        draggable: true,
                    });
                }}
                onContextMenuPreparing={(e) => {
                    if (e.field && e.field.area === 'data') {
                        const dataSource = e.component.getDataSource();
                        const changeSummaryType = (clickedItem: {
                            itemData: { value: string },
                        }) => {
                            dataSource.field((e.field as any).index, {
                                summaryType: clickedItem.itemData.value,
                            })
                            dataSource.load();
                        }
    
                        const items = [
                            { text: 'Sum', value: 'sum', onItemClick: changeSummaryType },
                            { text: 'Avg', value: 'avg', onItemClick: changeSummaryType },
                            { text: 'Min', value: 'min', onItemClick: changeSummaryType },
                            { text: 'Max', value: 'max', onItemClick: changeSummaryType },
                            { text: 'Count', value: 'count', onItemClick: changeSummaryType },
                        ];
    
                        Array.prototype.push.apply(e.items!, items);
                    }
                }}
            />
            <Show when={!context.loading()}>
                <PivotGridFieldChooser
                    dataSource={props.dataSource}
                    allowSearch
                    condensed
                />
            </Show>
        </div>
    )
}

const BasicView = (props: { store: DevExpress.data.CustomStore<any, unknown> }) => {
    const context = requireReportResultsContext();

    return (
        <DataGrid
            loading={context.loading()}
            showBorders={true}
            showRowLines={true}
            allowColumnReordering={true}
            allowColumnResizing={true}
            columnAutoWidth={true}
            scrolling={{
                showScrollbar: 'always',
            }}
            headerFilter={{
                visible: true,
                search: {
                    enabled: true,
                }
            }}
            dataSource={props.store}
            paging={{
                pageSize: 10
            }}
            pager={{
                showPageSizeSelector: true,
                allowedPageSizes: [10, 20, 50, 100],
                showInfo: true,
                showNavigationButtons: true,
            }}
            export={{
                enabled: true,
            }}
        >
            <DataGrid.Toolbar visible>
                <DataGrid.PredefinedToolbarItem name='exportButton' />
            </DataGrid.Toolbar>
        </DataGrid>
    )
}


ReportResults.defaultProps = new ReportResultsProps();

const ReportResultsModal = (props: { processQueueGu: string }) => {
    return (
        <div style={{ width: '80vw', height: '80vh' }}>
            <ReportResults value={props.processQueueGu} />
        </div>
    );
}

export const launchInteractiveReportResults = (processQueueGu: string, jobID: string | undefined) => {
    createRoot(() => {
        showModal(() => <ReportResultsModal processQueueGu={processQueueGu} />, {
            title: jobID || 'Report Results',
            draggable: true,
        });
    })
}

window.ST.JobQueue = {
    ...(window.ST.JobQueue || {}),
    LaunchInteractiveReportResults: launchInteractiveReportResults,
}